import DOMPurify from 'dompurify'

/**
 * Server text that is shown with v-html goes through sanitizeHtml(), with the whitelist keyvue-api stores comments
 * with (CommentHtmlSanitizer); only an e-mail template preview uses sanitizeEmailPreviewHtml() (below):
 *
 * - text tags without attributes: p, br, b, strong, i, em, u, s, ul, ol, li, h1, h2, h3, blockquote, pre, code, div;
 * - a, with an http(s), mailto or tel href or a relative one, and target="_blank" (with rel="noopener noreferrer");
 * - span, for the @mentions of keyvue-app and one-app: class mention, keyvue-mention or ql-mention-denotation-char,
 *   data-id, data-value, data-denotation-char, data-type and contenteditable="false".
 *
 * Other elements are unwrapped (their text stays), script, style, iframe, ... go with their content, and other
 * attributes go.
 */
const ALLOWED_TAGS = [
    'p', 'br', 'b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'blockquote', 'pre', 'code', 'div',
    'a', 'span',
]

const LINK_ATTRIBUTES = ['href', 'target', 'rel']

const MENTION_ATTRIBUTES = ['class', 'data-id', 'data-value', 'data-denotation-char', 'data-type', 'contenteditable']

const MENTION_CLASSES = ['mention', 'keyvue-mention', 'ql-mention-denotation-char']

/**
 * http(s), mailto or tel, or relative: no scheme before the first "/", "?" or "#", and no leading backslash.
 * DOMPurify tests an href without whitespace and control characters ("java\tscript:" is javascript:).
 */
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z\\]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i

const CONFIG = {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...LINK_ATTRIBUTES, ...MENTION_ATTRIBUTES],
    // Only href is a url; the values of the other attributes are checked in the hooks below
    ADD_URI_SAFE_ATTR: ['target', 'rel', ...MENTION_ATTRIBUTES],
    ALLOWED_URI_REGEXP,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
}

// An instance of its own, so that these hooks apply to sanitizeHtml() only
const purifier = DOMPurify(window)

purifier.addHook('uponSanitizeAttribute', (node, data) => {
    const tagName = node.nodeName.toLowerCase()

    if (LINK_ATTRIBUTES.includes(data.attrName)) {
        data.keepAttr = tagName === 'a'
            && (data.attrName !== 'target' || data.attrValue.trim().toLowerCase() === '_blank')

        return
    }

    if (data.attrName === 'class') {
        const classes = data.attrValue.split(/\s+/).filter(name => MENTION_CLASSES.includes(name))
        data.attrValue = classes.join(' ')
        data.keepAttr = tagName === 'span' && classes.length > 0

        return
    }

    if (data.attrName === 'contenteditable') {
        data.keepAttr = tagName === 'span' && data.attrValue === 'false'

        return
    }

    if (MENTION_ATTRIBUTES.includes(data.attrName)) {
        data.keepAttr = tagName === 'span'
    }
})

/**
 * A link that opens a new tab gets rel="noopener noreferrer"; rel goes without target.
 */
function forceNoopener(node: Element): void {
    if (node.nodeName.toLowerCase() !== 'a') {
        return
    }

    if (node.hasAttribute('target')) {
        node.setAttribute('target', '_blank')
        node.setAttribute('rel', 'noopener noreferrer')
    } else {
        node.removeAttribute('rel')
    }
}

purifier.addHook('afterSanitizeAttributes', forceNoopener)

export function sanitizeHtml(html: string|null|undefined): string {
    if (!html) {
        return ''
    }

    return purifier.sanitize(html, CONFIG)
}

/**
 * Style properties that move an element or take it out of the flow, so that it could lie over the app
 */
const POSITIONING_PROPERTIES = [
    'position', 'z-index', 'top', 'right', 'bottom', 'left', 'inset', 'transform', 'translate', 'rotate', 'scale',
    'zoom',
]

/** A positioning property (also with a vendor prefix), inset-* or a motion path (offset, offset-*) */
function isPositioningProperty(property: string): boolean {
    const name = property.trim().toLowerCase().replace(/^-[a-z]+-/, '')

    return POSITIONING_PROPERTIES.includes(name) || name.startsWith('inset-')
        || name === 'offset' || name.startsWith('offset-')
}

/**
 * The properties the browser itself reads from a style attribute: the same parser that will apply it, so an escape
 * ("posit\ion"), a comment or an odd quote reads the same here as there
 */
function parsedProperties(style: string): CSSStyleDeclaration {
    const probe = document.createElement('div')
    probe.setAttribute('style', style)

    return probe.style
}

function hasPositioning(style: string): boolean {
    const declaration = parsedProperties(style)
    for (let index = 0; index < declaration.length; index++) {
        if (isPositioningProperty(declaration.item(index))) {
            return true
        }
    }

    return false
}

/** The declarations of a style attribute: split at each ";" that is not in a string, in brackets (url()) or escaped */
function splitDeclarations(style: string): string[] {
    const declarations: string[] = []
    let current = ''
    let quote: string|null = null
    let depth = 0

    for (let index = 0; index < style.length; index++) {
        const character = style[index]
        if (character === '\\') {
            current += character + (style[index + 1] || '')
            index++
            continue
        }

        if (quote !== null) {
            quote = character === quote ? null : quote
        } else if (character === '"' || character === '\'') {
            quote = character
        } else if (character === '(') {
            depth++
        } else if (character === ')' && depth > 0) {
            depth--
        } else if (character === ';' && depth === 0) {
            declarations.push(current)
            current = ''
            continue
        }

        current += character
    }

    declarations.push(current)

    return declarations.filter(declaration => declaration.trim() !== '')
}

/**
 * A style attribute without positioning (POSITIONING_PROPERTIES). Whether there is any is up to the browser's CSS
 * parser; without, the style stays exactly as written (url(data:...;base64,...) and mso-* for Outlook included).
 * With, the declarations go that set positioning or don't start with a plain property name; should the browser still
 * read positioning in what is left, it removes that itself (and then the properties it doesn't know go too).
 */
function removePositioning(style: string): string {
    if (!hasPositioning(style)) {
        return style
    }

    const kept = splitDeclarations(style)
        .filter(part => {
            const colon = part.indexOf(':')
            const property = colon < 0 ? '' : part.slice(0, colon).trim().toLowerCase()

            return /^-?[a-z][a-z0-9-]*$/.test(property) && !isPositioningProperty(property)
        })
        .map(part => part.trim())
        .join('; ')

    if (!hasPositioning(kept)) {
        return kept
    }

    const parsed = parsedProperties(kept)
    const positioning: string[] = []
    for (let index = 0; index < parsed.length; index++) {
        if (isPositioningProperty(parsed.item(index))) {
            positioning.push(parsed.item(index))
        }
    }
    positioning.forEach(property => parsed.removeProperty(property))

    return parsed.cssText
}

/**
 * Quill's format classes (alignment, indent, direction, size, font): kept when a template goes into the editor
 */
const QUILL_FORMAT_CLASS = /^ql-(align|indent|direction|size|font)-[a-z0-9-]+$/

/**
 * A project's e-mail template, shown as a preview (sanitizeEmailPreviewHtml) or loaded into the template editor
 * (sanitizeEmailTemplateForEditor). DOMPurify's HTML profile, so tables, images and inline styles (colours, fonts,
 * widths) stay, while script, event handlers and javascript: urls go. Also removed: style elements (they would
 * restyle the whole app), forms and their fields, iframes, objects, srcset, id, and in a style attribute
 * positioning (removePositioning()), so that a template can't lay elements over the app. target is "_blank" only,
 * with rel="noopener noreferrer".
 *
 * The preview also drops class attributes and only shows an http(s) or data:image image. The editor keeps Quill's
 * format classes and any src DOMPurify allows (relative too): a template has Twig placeholders such as
 * <img src="{{ branding.logoUrl }}">, which the editor has to keep.
 */
function createEmailTemplatePurifier(forEditor: boolean) {
    const templatePurifier = DOMPurify(window)

    templatePurifier.addHook('uponSanitizeAttribute', (node, data) => {
        if (data.attrName === 'style') {
            data.attrValue = removePositioning(data.attrValue)
            data.keepAttr = data.attrValue.trim() !== ''

            return
        }

        if (data.attrName === 'target') {
            data.keepAttr = data.attrValue.trim().toLowerCase() === '_blank'

            return
        }

        if (data.attrName === 'class' && forEditor) {
            const classes = data.attrValue.split(/\s+/).filter(name => QUILL_FORMAT_CLASS.test(name))
            data.attrValue = classes.join(' ')
            data.keepAttr = classes.length > 0

            return
        }

        if (data.attrName === 'src' && !forEditor) {
            const src = data.attrValue.trim()
            data.keepAttr = /^https?:/i.test(src)
                || (node.nodeName.toLowerCase() === 'img' && /^data:image\//i.test(src))
        }
    })

    templatePurifier.addHook('afterSanitizeAttributes', forceNoopener)

    return templatePurifier
}

const EMAIL_TEMPLATE_CONFIG = {
    USE_PROFILES: { html: true },
    ADD_ATTR: ['target'],
    FORBID_TAGS: [
        'style', 'form', 'input', 'button', 'select', 'option', 'optgroup', 'textarea', 'datalist', 'fieldset',
        'legend', 'output', 'iframe', 'frame', 'frameset', 'object', 'embed',
    ],
    FORBID_ATTR: ['srcset', 'id'],
}

const emailPreviewPurifier = createEmailTemplatePurifier(false)
const emailEditorPurifier = createEmailTemplatePurifier(true)

/**
 * For the preview of a project's e-mail template, shown in a box with class email-preview (App.vue), which keeps
 * anything that is still out of place (a negative margin, a very wide element) inside it.
 */
export function sanitizeEmailPreviewHtml(html: string|null|undefined): string {
    if (!html) {
        return ''
    }

    return emailPreviewPurifier.sanitize(html, { ...EMAIL_TEMPLATE_CONFIG, FORBID_ATTR: ['srcset', 'id', 'class'] })
}

/**
 * For a stored template that goes into the template editor (Quill, which parses it in the page)
 */
export function sanitizeEmailTemplateForEditor(html: string|null|undefined): string {
    if (!html) {
        return ''
    }

    return emailEditorPurifier.sanitize(html, EMAIL_TEMPLATE_CONFIG)
}

/**
 * Text as HTML that shows exactly that text, e.g. a name that is put into HTML that is shown with v-html.
 */
export function escapeHtml(text: string|null|undefined): string {
    if (!text) {
        return ''
    }

    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
}

/**
 * Text with its character references (&amp;, &lt;, &eacute;, &#8364;, ...) as the characters, in one pass, so
 * "&amp;lt;" becomes "&lt;"; for a stored comment that is edited as text. The browser decodes them: the content of a
 * textarea is text only, so no element is made and nothing runs.
 */
export function decodeHtmlEntities(text: string): string {
    const textarea = document.createElement('textarea')
    textarea.innerHTML = text

    return textarea.value
}
